import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchExpenseLedger, fetchPeriods, setExpenseFlag, fetchSalaryMap, exportXlsx } from "../api";
import {
  PageHeader, Card, Select, SearchBox, ChipToggle, Segmented,
  Loading, ErrorState, EmptyState, useHorizontalScrollShadow,
} from "../components/ui";
import { money0, acct, fmtPeriodShort, num, truncate } from "../lib/format";
import { readUi, writeUi, clearUi } from "../lib/persist";

/**
 * Expenses, as one drill-down grid: each account is a row of period totals that
 * opens to reveal its transactions, with every amount under the period it
 * belongs to.
 *
 * This began as a second page built alongside the original flat list so the two
 * could be compared. It won, and the original was removed.
 */

const ACCOUNT_ORDER = [
  "Revenue", "Staff Salaries", "Subcontractors", "Travel & Lodging",
  "Databases", "Comms/Cons", "Partner Drawings",
];

const YN_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Flagged" },
  { value: "no",  label: "Unflagged" },
];

/** Beyond this an expanded account is trimmed - refine the filters instead. */
const MAX_CHILDREN = 300;

const orderPeriods = (periods) =>
  [...periods].sort((a, b) => {
    const [ma, ya] = a.split("/");
    const [mb, yb] = b.split("/");
    return ya !== yb ? yb - ya : ma - mb;
  });

const newestFirst = (periods) =>
  [...periods].sort((a, b) => {
    const [ma, ya] = a.split("/");
    const [mb, yb] = b.split("/");
    return yb - ya || mb - ma;
  });

const defaultPeriod = (periods) => {
  const ranked = newestFirst(periods.filter(Boolean));
  return ranked.find((p) => parseInt(p.split("/")[0], 10) <= 12) || ranked[0];
};

export default function Expenses({ username, colScale = 1 }) {
  const [expenses, setExpenses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const isFd = username === "fd";
  // Shares the Expenses page's remembered selection - same data, two views.
  const [selectedPeriods, setSelectedPeriods] = useState(() => readUi("exp.periods", []));
  const [manager, setManager] = useState(() => readUi("exp.manager", ""));
  const [focusedYear, setFocusedYear] = useState(() => readUi("exp.year", ""));
  const [project, setProject] = useState("");
  const [ynFilter, setYnFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const [salaryMap, setSalaryMap] = useState([]);
  // "tri": group a person's salary rows onto one line (multi-month only).
  // "off": always the one-row-per-transaction view.
  const [salaryView, setSalaryView] = useState("tri");
  const [exporting, setExporting] = useState(false);
  const scrollRef = useHorizontalScrollShadow();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([fetchExpenseLedger(username), fetchPeriods(username).catch(() => [])])
      .then(([rows, periodList]) => {
        const data = (rows || []).filter((e) => e.account_name !== "revenue");
        setExpenses(data);

        const unique = orderPeriods([...new Set(data.map((e) => e.period).filter(Boolean))]);
        setPeriods(unique);

        setSelectedPeriods((prev) => {
          const valid = prev.filter((p) => unique.includes(p));
          if (valid.length) return valid;
          const latest = defaultPeriod(periodList?.length ? periodList : unique);
          return latest ? [latest] : [];
        });
        if (unique.length) {
          const years = new Set(unique.map((p) => p.split("/")[1]));
          setFocusedYear((prev) => (prev && years.has(prev) ? prev : unique[0].split("/")[1]));
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(() => {
    fetchSalaryMap()
      .then(setSalaryMap)
      .catch(() => setSalaryMap([]));
  }, []);

  useEffect(load, [load]);
  useEffect(() => { writeUi("exp.periods", selectedPeriods); }, [selectedPeriods]);
  useEffect(() => { writeUi("exp.manager", manager); }, [manager]);
  useEffect(() => { if (focusedYear) writeUi("exp.year", focusedYear); }, [focusedYear]);

  const years = useMemo(
    () => [...new Set(periods.map((p) => p.split("/")[1]))].sort((a, b) => b - a),
    [periods]
  );

  const visiblePeriods = useMemo(
    () => (focusedYear ? periods.filter((p) => p.split("/")[1] === focusedYear) : periods),
    [periods, focusedYear]
  );

  const managers = useMemo(
    () => [...new Set([...expenses.map((e) => e.username), manager].filter(Boolean))].sort(),
    [expenses, manager]
  );

  /** Column order: the chosen periods, oldest first so they read left to right. */
  const cols = useMemo(
    () => [...selectedPeriods].sort((a, b) => {
      const [ma, ya] = a.split("/");
      const [mb, yb] = b.split("/");
      return ya !== yb ? ya - yb : ma - mb;
    }),
    [selectedPeriods]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return expenses.filter((e) => {
      if (selectedPeriods.length && !selectedPeriods.includes(e.period)) return false;
      if (manager && e.username !== manager) return false;
      if (project && e.project_name !== project) return false;
      if (ynFilter === "yes" && e.y_n?.toLowerCase() !== "y") return false;
      if (ynFilter === "no" && e.y_n?.toLowerCase() === "y") return false;
      if (needle && !(e.description || "").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [expenses, selectedPeriods, manager, project, ynFilter, search]);

  const projects = useMemo(
    () => [...new Set(
      expenses
        .filter((e) => (!selectedPeriods.length || selectedPeriods.includes(e.period))
                    && (!manager || e.username === manager))
        .map((e) => e.project_name).filter(Boolean)
    )].sort(),
    [expenses, selectedPeriods, manager]
  );

  /** Account -> period totals, plus its transactions. */
  const groups = useMemo(() => {
    const byAccount = new Map();
    filtered.forEach((e) => {
      const key = e.account_name || "(no account)";
      if (!byAccount.has(key)) {
        byAccount.set(key, { account: key, cells: {}, total: 0, rows: [] });
      }
      const g = byAccount.get(key);
      const amt = num(e.amount);
      g.cells[e.period] = (g.cells[e.period] || 0) + amt;
      g.total += amt;
      g.rows.push(e);
    });

    return [...byAccount.values()]
      .map((g) => ({
        ...g,
        rows: g.rows.sort((a, b) => {
          if (a.period !== b.period) {
            const [ma, ya] = a.period.split("/");
            const [mb, yb] = b.period.split("/");
            return ya !== yb ? ya - yb : ma - mb;
          }
          return Math.abs(num(b.amount)) - Math.abs(num(a.amount));
        }),
      }))
      .sort((a, b) => {
        const ia = ACCOUNT_ORDER.indexOf(a.account);
        const ib = ACCOUNT_ORDER.indexOf(b.account);
        const ra = ia === -1 ? ACCOUNT_ORDER.length : ia;
        const rb = ib === -1 ? ACCOUNT_ORDER.length : ib;
        return ra !== rb ? ra - rb : a.account.localeCompare(b.account);
      });
  }, [filtered]);

  /**
   * head -> mapped persons, longest name first so a longer name wins a match
   * over a shorter prefix (e.g. "Charlotte" before "Char").
   */
  const personMap = useMemo(() => {
    const m = new Map();
    (salaryMap || []).forEach(({ head, person }) => {
      const p = String(person || "").trim().toLowerCase();
      if (!p) return;
      if (!m.has(head)) m.set(head, []);
      m.get(head).push(p);
    });
    m.forEach((list, head) => m.set(head, list.sort((a, b) => b.length - a.length)));
    return m;
  }, [salaryMap]);

  const matchPerson = (list, description) => {
    const d = String(description || "").toLowerCase();
    return list.find((p) => d.includes(p)) || null;
  };

  /**
   * In multi-month grouped view, collapse one person's rows (per project) in an
   * account onto a single line. Rows that match no mapped person pass through
   * unchanged so they still show individually.
   */
  const salaryRowsFor = (g) => {
    const persons = personMap.get(g.account);
    if (!persons) return null; // this account isn't in the map
    const byKey = new Map();
    const others = [];
    g.rows.forEach((r) => {
      const person = matchPerson(persons, r.description);
      if (!person) { others.push(r); return; }
      const key = `${r.username || ""}\u0000${person}\u0000${r.project_name || ""}\u0000${g.account}`;
      if (!byKey.has(key)) {
        byKey.set(key, { _person: person, _project: r.project_name || "", _username: r.username || "", cells: {}, total: 0, rows: [] });
      }
      const slot = byKey.get(key);
      const amt = num(r.amount);
      slot.cells[r.period] = (slot.cells[r.period] || 0) + amt;
      slot.total += amt;
      slot.rows.push(r);
    });
    const personRows = [...byKey.values()].sort((a, b) => {
      if (a._person !== b._person) return a._person.localeCompare(b._person);
      if ((a._username || "") !== (b._username || "")) return (a._username || "").localeCompare(b._username || "");
      return (a._project || "").localeCompare(b._project || "");
    }).map((slot) => ({
      ...slot,
      flagged: slot.rows.length > 0 && slot.rows.every((r) => r.y_n?.toLowerCase() === "y"),
    }));
    return { personRows, others };
  };

  const grouped = cols.length > 1 && salaryView === "tri";

  const totals = useMemo(() => {
    const expense = groups.filter((g) => g.account !== "Revenue");
    const revenue = groups.filter((g) => g.account === "Revenue");
    const sumAt = (list, p) => list.reduce((s, g) => s + (g.cells[p] || 0), 0);
    return {
      expenses: {
        cells: Object.fromEntries(cols.map((p) => [p, sumAt(expense, p)])),
        total: expense.reduce((s, g) => s + g.total, 0),
      },
      net: {
        cells: Object.fromEntries(cols.map((p) => [p, sumAt(revenue, p) - sumAt(expense, p)])),
        total: revenue.reduce((s, g) => s + g.total, 0) - expense.reduce((s, g) => s + g.total, 0),
      },
    };
  }, [groups, cols]);

  const togglePeriod = (p) =>
    setSelectedPeriods((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const toggleAccount = (account) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(account)) next.delete(account); else next.add(account);
      return next;
    });

  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.account));
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.account)));

  const clearAll = () => {
    clearUi("exp.periods");
    clearUi("exp.manager");
    setManager(""); setSelectedPeriods([]); setProject("");
    setYnFilter("all"); setSearch(""); setOpen(new Set());
  };

  const toggleFlag = async (row) => {
    const next = row.y_n?.toLowerCase() === "y" ? "n" : "y";
    const prev = row.y_n;
    setExpenses((rows) => rows.map((r) => (r.id === row.id ? { ...r, y_n: next } : r)));
    try {
      await setExpenseFlag(row.id, next);
    } catch (e) {
      setExpenses((rows) => rows.map((r) => (r.id === row.id ? { ...r, y_n: prev } : r)));
      setNotice(`Could not update flag: ${e.message}`);
    }
  };

  /**
   * Flatten the grid into rows Excel can pivot on. The hierarchy survives as a
   * Level column plus a filled-in Account on every line, so the sheet is
   * useful with or without grouping.
   *
   * Two deliberate differences from the screen: expand/collapse is ignored
   * (that is a screen-space convenience, not a filter) and the MAX_CHILDREN
   * cap is not applied, so the workbook carries every transaction behind the
   * numbers. Filters and the Grouped/Ungrouped choice ARE respected.
   */
  const buildExportRows = () => {
    const out = [];
    const cells = (obj) => Object.fromEntries(cols.map((p) => [p, obj[p] ?? null]));

    // Leaf lines only. The account header totals are deliberately NOT emitted:
    // they restate the same money as the lines beneath them, so a sheet
    // carrying both double-counts the moment you sum or pivot the Total column.
    // The account travels on every line instead, as a column you can group by.
    groups.forEach((g) => {
      const salary = grouped ? salaryRowsFor(g) : null;

      if (salary) {
        salary.personRows.forEach((pr) => {
          out.push({
            level: "Person",
            account: g.account,
            detail: pr._person,
            manager: (pr._username || "").toUpperCase(),
            project: pr._project,
            flagged: pr.flagged ? "Y" : "",
            ...cells(pr.cells),
            total: pr.total,
          });
        });
      }

      (salary ? salary.others : g.rows).forEach((r) => {
        out.push({
          level: "Transaction",
          account: g.account,
          detail: r.description || "",
          manager: (r.username || "").toUpperCase(),
          project: r.project_name || "",
          flagged: r.y_n?.toLowerCase() === "y" ? "Y" : "",
          ...cells({ [r.period]: num(r.amount) }),
          total: num(r.amount),
        });
      });
    });

    return out;
  };

  const handleExport = async () => {
    setExporting(true);
    setNotice("");
    try {
      const scope = manager ? manager.toUpperCase() : String(username).toUpperCase();
      const range = cols.length
        ? `${fmtPeriodShort(cols[0])}${cols.length > 1 ? `-${fmtPeriodShort(cols[cols.length - 1])}` : ""}`
        : "";
      await exportXlsx({
        filename: `Expenses ${scope} ${range} ${new Date().toISOString().slice(0, 10)}`.replace(/\s+/g, " ").trim(),
        sheet: "Expenses",
        title: `Expenses — ${scope}${range ? ` — ${range}` : ""}`
             + (project ? ` — ${project}` : "")
             + (grouped ? " (salary rows grouped by person)" : ""),
        columns: [
          { key: "level",   label: "Level",   type: "text" },
          { key: "account", label: "Account", type: "text" },
          { key: "detail",  label: "Description / person", type: "text" },
          ...(isFd ? [{ key: "manager", label: "Manager", type: "text" }] : []),
          { key: "project", label: "Project", type: "text" },
          { key: "flagged", label: "Flagged", type: "text" },
          ...cols.map((p) => ({ key: p, label: fmtPeriodShort(p), type: "money" })),
          { key: "total",   label: "Total",   type: "money" },
        ],
        rows: buildExportRows(),
        totals: {
          ...Object.fromEntries(cols.map((p) => [p, totals.expenses.cells[p] || 0])),
          total: totals.expenses.total,
        },
      });
    } catch (e) {
      setNotice(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <main className="page"><Loading label="Loading expenses…" /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  const span = cols.length + 2;

  return (
    <main className="page page--wide">
      <PageHeader
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            Expenses
            <Select
              value={focusedYear}
              onChange={setFocusedYear}
              options={years.map((y) => ({ value: y, label: y }))}
              style={{ minWidth: 80 }}
            />
          </span>
        }
        subtitle={`${money0(totals.expenses.total)} · ${groups.length} account${groups.length === 1 ? "" : "s"} · ${filtered.length} transactions`}
      >
        {isFd && (
          <Select
            value={manager}
            onChange={(v) => { setManager(v); setProject(""); }}
            options={[
              { value: "", label: "All managers" },
              ...managers.map((m) => ({ value: m, label: m.toUpperCase() })),
            ]}
            style={{ minWidth: 118 }}
          />
        )}
        <Select
          value={project}
          onChange={setProject}
          options={[{ value: "", label: "All projects" }, ...projects.map((p) => ({ value: p, label: p }))]}
          style={{ maxWidth: 240 }}
        />
        <Select value={ynFilter} onChange={setYnFilter} options={YN_OPTIONS} style={{ minWidth: 90 }} />
        <SearchBox value={search} onChange={setSearch} placeholder="Search descriptions…" />
        {cols.length > 1 && personMap.size > 0 && (
          <Segmented
            value={salaryView}
            onChange={setSalaryView}
            options={[
              { value: "tri", label: "Grouped" },
              { value: "off", label: "Ungrouped" },
            ]}
          />
        )}
        <button className="btn btn--danger btn--sm" onClick={clearAll}>Clear all</button>
        <button
          className="btn btn--sm"
          onClick={handleExport}
          disabled={exporting || !groups.length}
        >
          {exporting ? "Preparing…" : "Export to Excel"}
        </button>
      </PageHeader>

      <div className="chipset chipset--compact" style={{ marginBottom: 12 }}>
        {visiblePeriods.map((p) => (
          <ChipToggle key={p} on={selectedPeriods.includes(p)} onClick={() => togglePeriod(p)}>
            {fmtPeriodShort(p)}
          </ChipToggle>
        ))}
      </div>

      {notice && <div className="notice notice--warn" style={{ marginBottom: 12 }}>{notice}</div>}

      {!selectedPeriods.length ? (
        <Card><EmptyState title="Pick a period" hint="Choose one or more periods above." /></Card>
      ) : !groups.length ? (
        <Card><EmptyState title="Nothing matches" hint="Try widening the filters." /></Card>
      ) : (
        <Card
          title="Accounts"
          note="click an account to see the transactions behind it"
          actions={
            <button className="btn btn--sm" onClick={toggleAll}>
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
          }
          flush
        >
          <div className="tbl-wrap tbl-wrap--flush x2-wrap" ref={scrollRef}>
            <table className="tbl x2-tbl">
              <thead>
                <tr>
                  <th className="sticky-col" style={{ minWidth: Math.round(320 * colScale) }}>
                    Account / transaction
                  </th>
                  {cols.map((p) => (
                    <th key={p} className="num" style={{ minWidth: Math.round(108 * colScale) }}>
                      {fmtPeriodShort(p)}
                    </th>
                  ))}
                  <th className="num" style={{ minWidth: Math.round(118 * colScale) }}>Total</th>
                </tr>
              </thead>

              <tbody>
                {groups.map((g) => {
                  const isOpen = open.has(g.account);
                  const salary = grouped ? salaryRowsFor(g) : null;
                  // Collapse has to win over grouping: a mapped account was
                  // previously stuck open because `shown` ignored isOpen.
                  const shown = !isOpen ? []
                    : (salary ? salary.others : g.rows.slice(0, MAX_CHILDREN));
                  return (
                    <React.Fragment key={g.account}>
                      <tr
                        className={`x2-account${isOpen ? " is-open" : ""}`}
                        onClick={() => toggleAccount(g.account)}
                      >
                        <td className="sticky-col">
                          <span className="tree-label">
                            <span className="tree-toggle" data-open={isOpen}>▶</span>
                            <span className="x2-account-name">{g.account}</span>
                            <span className="x2-count">{g.rows.length}</span>
                          </span>
                        </td>
                        {cols.map((p) => (
                          <td key={p} className="num">
                            {g.cells[p] ? money0(g.cells[p]) : <span className="dash">—</span>}
                          </td>
                        ))}
                        <td className="num x2-total">{money0(g.total)}</td>
                      </tr>

                      {isOpen && salary && salary.personRows.map((pr) => (
                        <tr key={`${pr._username}\u0000${pr._person}\u0000${pr._project}`} className="x2-txn">
                          <td className="sticky-col x2-txn-name">
                            <span className="x2-line">
                              {isFd && pr._username && (
                                <span className="badge">{String(pr._username).toUpperCase()}</span>
                              )}
                              <span className="x2-desc">{pr._person}</span>
                              <span className="truncate" title={pr._project}>{pr._project}</span>
                              <input
                                className="cbx"
                                type="checkbox"
                                title="Flag"
                                checked={pr.flagged}
                                disabled
                              />
                            </span>
                          </td>
                          {cols.map((p) => (
                            <td key={p} className="num">
                              {pr.cells[p] ? money0(pr.cells[p]) : <span className="dash">—</span>}
                            </td>
                          ))}
                          <td className="num x2-total">{money0(pr.total)}</td>
                        </tr>
                      ))}

                      {shown.map((r) => (
                        <tr key={r.id} className="x2-txn">
                          <td className="sticky-col x2-txn-name">
                            <span className="x2-line">
                              {isFd && r.username && (
                                <span className="badge">{String(r.username).toUpperCase()}</span>
                              )}
                              <span className="x2-desc" title={r.description}>
                                {truncate(r.description, 78) || <span className="dash">—</span>}
                              </span>
                              <span className="truncate" title={r.project_name}>{r.project_name}</span>
                              <input
                                className="cbx"
                                type="checkbox"
                                title="Flag"
                                checked={r.y_n?.toLowerCase() === "y"}
                                onChange={() => toggleFlag(r)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </span>
                          </td>
                          {cols.map((p) => (
                            <td key={p} className="num">
                              {r.period === p ? money0(r.amount) : <span className="dash">·</span>}
                            </td>
                          ))}
                          <td className="num">{money0(r.amount)}</td>
                        </tr>
                      ))}

                      {isOpen && !salary && g.rows.length > MAX_CHILDREN && (
                        <tr className="x2-txn x2-more">
                          <td colSpan={span}>
                            Showing the first {MAX_CHILDREN} of {g.rows.length} transactions —
                            narrow the period, project or search to see the rest.
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td className="sticky-col">Total expenses</td>
                  {cols.map((p) => (
                    <td key={p} className="num">{money0(totals.expenses.cells[p] || 0)}</td>
                  ))}
                  <td className="num">{money0(totals.expenses.total)}</td>
                </tr>
                <tr>
                  <td className="sticky-col">Net (revenue − expenses)</td>
                  {cols.map((p) => {
                    const v = totals.net.cells[p] || 0;
                    return <td key={p} className={`num ${v >= 0 ? "pos" : "neg"}`}>{acct(v)}</td>;
                  })}
                  <td className={`num ${totals.net.total >= 0 ? "pos" : "neg"}`}>
                    {acct(totals.net.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </main>
  );
}
